import type { Speaker, Transcript } from "@prisma/client";
import type { TranscribingResult } from "./types/diarization";

/**
 * Speech-to-text provider enum matching backend STTProvider.
 * Values must match the C# enum exactly.
 */
export enum STTProvider {
	OAI_Whisper = 1,
	OAI_GPT_4o_Transcribe = 2,
	OAI_GPT_4oMini_Transcribe = 3,
	AZAI_Fast_Transcription = 50,
}

/**
 * Speech-to-text domain enum matching backend STTDomain.
 * Values must match the C# enum exactly.
 * Domain only applies to OAI providers (Whisper, GPT-4o, GPT-4o Mini),
 * not to AZAI_Fast_Transcription.
 */
export enum STTDomain {
	None = 0,
	Math = 1,
}

/** Providers that support STTDomain selection */
const DOMAIN_SUPPORTED_PROVIDERS = new Set<STTProvider>([
	STTProvider.OAI_Whisper,
	STTProvider.OAI_GPT_4o_Transcribe,
	STTProvider.OAI_GPT_4oMini_Transcribe,
]);

/** Check if a provider supports domain selection */
export function supportsDomain(provider: STTProvider): boolean {
	return DOMAIN_SUPPORTED_PROVIDERS.has(provider);
}

/** Human-readable labels for each STT provider */
export const STT_PROVIDER_LABELS: Record<STTProvider, string> = {
	[STTProvider.OAI_Whisper]: "Allicorn Model 1",
	[STTProvider.OAI_GPT_4o_Transcribe]: "GPT-4o",
	[STTProvider.OAI_GPT_4oMini_Transcribe]: "GPT-4o Mini",
	[STTProvider.AZAI_Fast_Transcription]: "Allicorn Model 2",
};

/** Helper to get model text from a transcript's modelTexts JSON field */
export function getModelText(
	modelTexts: Record<string, string> | null | undefined,
	provider: STTProvider
): string | null {
	if (!modelTexts) return null;
	return modelTexts[String(provider)] ?? null;
}

/**
 * SignalR connection configuration options
 */
export interface SignalRConnectionOptions {
	url: string;
	roomId: string;
	userId: string;
}

/**
 * Transcribing message - sent while transcription is in progress
 */
export interface TranscribingMessage {
	type: "transcribing";
	data: TranscribingResult;
}

/**
 * Transcribed message - sent when transcription is complete
 */
export interface TranscribedMessage {
	type: "transcribed";
	data: Transcript & { Speaker: Speaker };
}

export interface TranscriptUpdatedMessage {
	type: "transcript-updated";
	data: Transcript;
}

export interface SpeakerUpdatedMessage {
	type: "speaker-updated";
	data: Speaker;
}

export interface RoomEndedMessage {
	type: "room-ended";
	data: { roomId: string };
}

export interface RoomCreatedMessage {
	type: "room-created";
	data: { roomId: string };
}

export interface EnhancedTextUpdatedMessage {
	type: "enhanced-text-updated";
	data: { transcriptId: number; enhancedText: string };
}

export interface ModelTextUpdatedMessage {
	type: "model-text-updated";
	data: {
		transcriptId: number;
		provider: STTProvider;
		text: string;
		finalTranscriptionAt?: number;
	};
}

export type SignalRBroadcastMessage =
	| TranscribingMessage
	| TranscribedMessage
	| TranscriptUpdatedMessage
	| SpeakerUpdatedMessage
	| RoomEndedMessage
	| RoomCreatedMessage
	| EnhancedTextUpdatedMessage
	| ModelTextUpdatedMessage;

/**
 * SignalR service event types
 */
export type SignalRServiceEvent =
	| "connected"
	| "disconnected"
	| "error"
	| "message";

/**
 * Request to re-transcribe audio segment using slower, more accurate model
 */
export interface TranscribeReq {
	RequestID: string;
	StartSec: number;
	EndSec: number;
	Model?: STTProvider;
	Domain?: STTDomain;
}

/**
 * Response from backend with improved transcription
 */
export interface TranscribeRes {
	requestID: string;
	text: string;
	language: string;
}
